/**
 * Escapes special HTML characters to prevent XSS when building HTML strings
 * from user-supplied data (names, emails, subjects, etc.).
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Detects if content is likely pure HTML or Markdown (with possible HTML inline).
 * More sophisticated heuristic: if content starts with HTML tags or is mostly HTML, treat as HTML.
 */
export function isHtmlContent(content: string): boolean {
  const trimmed = content.trim();

  // If content starts with HTML tags, it's likely pure HTML
  if (/^<[a-z]/i.test(trimmed)) {
    return true;
  }

  // If content is mostly HTML tags (more than 50% of non-whitespace chars are HTML)
  const htmlChars = (trimmed.match(/<[^>]*>/g) || []).join('').length;
  const nonWhitespaceChars = trimmed.replace(/\s/g, '').length;

  if (nonWhitespaceChars > 0 && htmlChars / nonWhitespaceChars > 0.5) {
    return true;
  }

  return false;
}

/**
 * Converts HTML to Markdown format.
 * Handles common HTML tags: links, bold, italic, line breaks, and removes other HTML tags.
 * Also decodes HTML entities.
 */
export function convertHtmlToMarkdown(html: string): string {
  if (!html) return '';

  const decodeHtmlEntities = (text: string): string => {
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }
    return text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'");
  };

  // Extract <pre> code blocks from the RAW html first: at this stage code angle brackets are
  // still entities (&lt;/&gt;), so stripping the inner tags is safe and won't corrupt the code.
  const codeBlocks: string[] = [];
  const htmlWithPlaceholders = html.replace(
    /<pre\b[^>]*>([\s\S]*?)<\/pre>/gi,
    (_, inner: string) => {
      const codeText = decodeHtmlEntities(
        inner
          .replace(/<\/(div|p)>/gi, '\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<[^>]+>/g, ''),
      ).replace(/\n+$/, '');
      const token = `\uE000CB${codeBlocks.length}\uE000`;
      codeBlocks.push(`\n\`\`\`\n${codeText}\n\`\`\`\n`);
      return token;
    },
  );

  // Decode first so contentEditable output like "&lt;br/&gt;" becomes real tags and can be normalized
  let md = decodeHtmlEntities(htmlWithPlaceholders);

  /** Strip HTML tags from a string, keeping the text content. */
  const stripTags = (s: string) => s.replace(/<[^>]*>/g, '');

  // --- inline formatting (before block-level conversions) ---

  // Bold: <strong>text</strong> or <b>text</b> → **text**
  md = md.replace(/<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi, '**$2**');

  // Italic: <em>text</em> or <i>text</i> → *text*
  md = md.replace(/<(em|i)\b[^>]*>([\s\S]*?)<\/\1>/gi, '*$2*');

  // Links: <a href="url">text</a> → [text](url)
  md = md.replace(
    /<a\s+(?:[^>]*?\s+)?href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_, url, text) => `[${stripTags(text).trim()}](${url})`,
  );

  // Inline code: <code>…</code> → `…`
  md = md.replace(/<code\b[^>]*>([\s\S]*?)<\/code>/gi, (_, inner: string) => {
    const text = stripTags(inner).replace(/`/g, '\\`').trim();
    return text ? `\`${text}\`` : '';
  });

  // Strikethrough: <s>, <strike>, <del> → ~~text~~
  md = md.replace(/<(s|strike|del)\b[^>]*>([\s\S]*?)<\/\1>/gi, (_, __, inner: string) => {
    const text = stripTags(inner).trim();
    return text ? `~~${text}~~` : '';
  });

  // Underline / span / font: keep text only (no native markdown underline)
  md = md.replace(/<u\b[^>]*>([\s\S]*?)<\/u>/gi, '$1');
  md = md.replace(/<span\b[^>]*>([\s\S]*?)<\/span>/gi, '$1');
  md = md.replace(/<font\b[^>]*>([\s\S]*?)<\/font>/gi, '$1');

  // --- line breaks ---
  md = md.replace(/<br\s*\/?>/gi, '\n');

  // --- block-level elements ---

  // Headings: <h1>…</h1> → # … (use [\s\S] to match across newlines)
  md = md.replace(/<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi, (_, level, text) => {
    const hashes = '#'.repeat(parseInt(level, 10));
    return `\n${hashes} ${stripTags(text).trim()}\n\n`;
  });

  // Lists: convert <li> to markdown items before stripping <ul>/<ol>
  // Unordered list items (GFM task-list items <li><input type="checkbox"> → "- [ ] " / "- [x] ")
  md = md.replace(/<ul\b[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner: string) => {
    const items = inner.replace(/<li\b[^>]*>([\s\S]*?)<\/li>/gi, (__, content: string) => {
      const checkbox = content.match(/<input\b[^>]*type\s*=\s*["']?checkbox["']?[^>]*>/i);
      if (checkbox) {
        const checked = /\bchecked\b/i.test(checkbox[0]);
        const text = stripTags(content.replace(checkbox[0], ''))
          .replace(/\u00A0/g, ' ')
          .trim();
        return `- [${checked ? 'x' : ' '}] ${text}\n`;
      }
      return `- ${stripTags(content).trim()}\n`;
    });
    return `\n${stripTags(items)}\n`;
  });

  // Ordered list items
  md = md.replace(/<ol\b[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner: string) => {
    let idx = 0;
    const items = inner.replace(/<li\b[^>]*>([\s\S]*?)<\/li>/gi, (__, content: string) => {
      idx++;
      return `${idx}. ${stripTags(content).trim()}\n`;
    });
    return `\n${stripTags(items)}\n`;
  });

  // Paragraphs: <p>…</p> → text\n\n
  md = md.replace(/<p\b[^>]*>([\s\S]*?)<\/p>/gi, '$1\n\n');

  // Blockquote: <blockquote>…</blockquote> → > line\n
  md = md.replace(/<blockquote\b[^>]*>([\s\S]*?)<\/blockquote>/gi, (_, inner: string) => {
    const text = stripTags(inner).trim();
    if (!text) return '\n';
    return `\n${text
      .split(/\r?\n/)
      .map((line) => `> ${line}`)
      .join('\n')}\n\n`;
  });

  // Horizontal rule
  md = md.replace(/<hr\b[^>]*\/?>/gi, '\n---\n');

  // Divs: contentEditable wraps each new line in <div>…</div> (the first line stays a bare
  // text node). The line break belongs BEFORE the content, otherwise a bare first line merges
  // with the first <div> (e.g. "a<div>b</div>" must become "a\nb", not "ab\n").
  md = md.replace(/<div\b[^>]*>([\s\S]*?)<\/div>/gi, '\n$1');

  // --- cleanup ---

  // Remove any remaining HTML tags (unknown / unsupported elements)
  md = md.replace(/<[^>]+>/g, '');

  // Decode again after stripping (e.g. entities inside removed tags)
  md = decodeHtmlEntities(md);

  // Collapse 3+ consecutive newlines to 2
  md = md.replace(/\n{3,}/g, '\n\n');

  // Restore extracted code blocks (after newline-collapsing so internal blank lines survive).
  if (codeBlocks.length) {
    md = md.replace(/\uE000CB(\d+)\uE000/g, (_, i: string) => codeBlocks[Number(i)] ?? '');
  }

  // Do not trim(): trailing/leading spaces are valid while typing; trim was eating spaces at line/document end.
  return md.replace(/\r\n/g, '\n');
}

/**
 * Markdown collapses multiple spaces in HTML output. Encode 2+ spaces (per line, after list/indent prefix)
 * using NBSP so marked preserves visual spacing. Preserves "two spaces + newline" hard breaks.
 */
export function preserveMultipleSpacesForMarked(md: string): string {
  if (!md) return '';

  const expandRun = (run: string): string => {
    let out = ' ';
    for (let i = 1; i < run.length; i++) {
      out += i % 2 === 1 ? '\u00A0' : ' ';
    }
    return out;
  };

  return md
    .split('\n')
    .map((line) => {
      const m = /^( *)(.*)$/.exec(line);
      if (!m) return line;
      const indent = m[1] ?? '';
      let rest = m[2] ?? '';
      let hardBreak = '';
      if (rest.endsWith('  ') && rest.length >= 2) {
        hardBreak = '  ';
        rest = rest.slice(0, -2);
      }
      const encodedRest = rest.replace(/ {2,}/g, expandRun);
      return indent + encodedRest + hardBreak;
    })
    .join('\n');
}

/**
 * Cleans HTML from a string, converting it to Markdown if HTML is detected,
 * otherwise returns the string as-is.
 */
export function cleanHtmlToMarkdown(content: string): string {
  if (!content) return '';

  // If content contains HTML tags, convert to Markdown
  if (/<[^>]+>/.test(content)) {
    return convertHtmlToMarkdown(content);
  }

  return content;
}
